/*!
 * SkyPlus Dashboard Component - Redesigned
 * - Removed: storage size scan, fake % badges, dead /tasks route
 * - Added: pending task counts (contract + mail), fixed donut chart
 * - Fast: only date-filtered uploads query + folder count
 */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SearchService } from '@alfresco/adf-content-services';
import { AuthenticationService } from '@alfresco/adf-core';
import { SearchRequest, ResultSetPaging } from '@alfresco/js-api';
import { SkyPlusAnalyticsService, AnalyticsSummary } from './skyplus-analytics.service';

interface RecentDocument {
  id: string; name: string; type: string; location: string;
  modifiedDate: Date; modifiedBy: string; icon: string;
}

interface QuickAction {
  id: string; label: string; icon: string; color: string; route: string; description: string;
}

interface WorkflowLauncher {
  id: string; name: string; description: string; icon: string; color: string; route: string;
}

@Component({
  selector: 'sp-dashboard',
  templateUrl: './skyplus-dashboard.component.html',
  styleUrls: ['./skyplus-dashboard.component.scss'],
  styles: [`
    .sp-an-head { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
    .sp-an-head .sp-card-header { margin:0; }
    .sp-an-range { display:flex; align-items:center; gap:8px; }
    .sp-an-range label { font-size:12px; color:#6c757d; }
    .sp-an-range select { font-size:12px; padding:4px 8px; border:1px solid #d7dbe0; border-radius:6px; background:#fff; color:#244062; cursor:pointer; }
    .sp-an-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
    .sp-an-sub { font-size:12px; color:#6c757d; margin:0 0 10px; }
    .sp-an-bars { display:flex; flex-direction:column; gap:9px; }
    .sp-an-bar-row { display:flex; align-items:center; gap:8px; font-size:12px; }
    .sp-an-bar-name { width:96px; color:#6c757d; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .sp-an-bar-track { flex:1; background:#eef1f4; border-radius:3px; height:14px; overflow:hidden; }
    .sp-an-bar-fill { background:#534AB7; height:14px; border-radius:3px; transition:width .3s ease; }
    .sp-an-bar-val { width:20px; text-align:right; color:#244062; font-weight:500; }
    .sp-an-empty { font-size:12px; color:#9aa0a6; padding:10px 0; }
    .sp-an-mini { margin-top:14px; display:flex; gap:10px; }
    .sp-an-mini-card { flex:1; background:#f6f8fa; border-radius:8px; padding:8px 10px; }
    .sp-an-mini-label { font-size:11px; color:#6c757d; }
    .sp-an-mini-val { font-size:18px; font-weight:500; color:#244062; }
    .sp-an-mini-val.danger { color:#dc3545; }
    .sp-an-trend svg { width:100%; height:110px; display:block; }
    .sp-an-foot { font-size:11px; color:#9aa0a6; margin-top:12px; border-top:1px solid #eef1f4; padding-top:8px; }
    @media (max-width:900px) { .sp-an-grid { grid-template-columns:1fr; } }
  `]
})
export class SkyPlusDashboardComponent implements OnInit, OnDestroy {

  greeting  = '';
  today     = new Date();
  isLoading = true;

  stats = {
    recentUploads:  0,
    pendingTasks:   0,
    myClaimable:    0,
    completed:      0
  };

  // ── Workflow analytics (native, from GET /api/wf/analytics/summary) ──────
  analyticsDays = 14;
  analyticsLoading = false;
  analytics: AnalyticsSummary | null = null;
  analyticsMaxByWorkflow = 1;
  analyticsTrendPoints = '';
  analyticsTrendLastX = 0;
  analyticsTrendLastY = 0;

  documentSummary: {
    type: string; count: number; color: string;
    percentage: number; dashArray: string; dashOffset: string;
  }[] = [];
  totalDocsForChart = 0;

  recentDocuments: RecentDocument[] = [];

  quickActions: QuickAction[] = [
    { id: 'documents', label: 'Browse documents',  icon: 'folder_open',   color: '#4A90D9', route: '/documents',         description: 'Open document library' },
    { id: 'upload',    label: 'Upload file',        icon: 'cloud_upload',  color: '#6c5ce7', route: '/documents',         description: 'Upload new files' },
    { id: 'search',    label: 'Advanced search',    icon: 'search',        color: '#e17055', route: '/documents',            description: 'Find documents' }
  ];

  workflowLaunchers: WorkflowLauncher[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private searchService: SearchService,
    private auth: AuthenticationService,
    private analyticsService: SkyPlusAnalyticsService
  ) {}

  ngOnInit(): void {
    this.setGreeting();
    this.loadDashboardData();
    this.loadAnalytics();
    this.loadActiveWorkflows();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setGreeting(): void {
    const h = new Date().getHours();
    this.greeting = h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
  }

  private loadActiveWorkflows(): void {
    this.analyticsService.getActiveWorkflows()
      .pipe(takeUntil(this.destroy$))
      .subscribe(launchers => { this.workflowLaunchers = (launchers ?? []).slice(0, 3); });
  }

  // ── Workflow analytics ───────────────────────────────────────────────────

  onAnalyticsDaysChange(days: number): void {
    if (days !== 7 && days !== 14 && days !== 30) return;
    this.analyticsDays = days;
    this.loadAnalytics();
  }

  private loadAnalytics(): void {
    this.analyticsLoading = true;
    this.analyticsService.getSummary(this.analyticsDays)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (s) => {
          this.analytics = s;
          this.stats.pendingTasks   = s.myPendingTasks;
          this.stats.myClaimable    = s.myClaimableTasks;
          this.stats.completed = s.myCompleted;
          this.computeAnalyticsViz(s);
          this.analyticsLoading = false;
        },
        error: () => { this.analytics = null; this.analyticsLoading = false; }
      });
  }

  /** Pre-computes bar widths and the SVG polyline for the completion trend. */
  private computeAnalyticsViz(s: AnalyticsSummary): void {
    this.analyticsMaxByWorkflow = Math.max(1, ...(s.tasksByWorkflow || []).map(w => w.value));

    const days = s.completionsByDay || [];
    const n = days.length;
    const maxV = Math.max(1, ...days.map(d => d.value));
    const w = 260, h = 100, padL = 6, padR = 6, padT = 10, padB = 14;
    const innerW = w - padL - padR;
    const innerH = h - padT - padB;

    const pts: string[] = days.map((d, i) => {
      const x = n <= 1 ? padL : padL + (i * innerW / (n - 1));
      const y = padT + innerH - (d.value / maxV) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    this.analyticsTrendPoints = pts.join(' ');

    if (pts.length) {
      const [lx, ly] = pts[pts.length - 1].split(',');
      this.analyticsTrendLastX = parseFloat(lx);
      this.analyticsTrendLastY = parseFloat(ly);
    } else {
      this.analyticsTrendLastX = 0;
      this.analyticsTrendLastY = 0;
    }
  }

  barWidth(value: number): string {
    const pct = this.analyticsMaxByWorkflow > 0 ? (value / this.analyticsMaxByWorkflow) * 100 : 0;
    return `${Math.round(pct)}%`;
  }

  private async loadDashboardData(): Promise<void> {
    this.isLoading = true;
    try {
      await Promise.all([
        this.loadRecentUploadsCount(),
        this.loadRecentDocuments(),
        this.loadDocumentSummary()
      ]);
    } catch (e) { console.error('Dashboard load error:', e); }
    finally { this.isLoading = false; }
  }

  private searchContent(query: string, maxItems = 1, sort?: any[]): Promise<ResultSetPaging | undefined> {
    const req: SearchRequest = {
      query: { query, language: 'afts' },
      paging: { maxItems, skipCount: 0 },
      include: ['path', 'properties']
    };
    if (sort) req.sort = sort;
    return this.searchService.searchByQueryBody(req).toPromise();
  }

  private async loadRecentUploadsCount(): Promise<void> {
    try {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      const dateStr = d.toISOString().split('T')[0];
      const me = ((this.auth as any).getEcmUsername?.() || '').trim();
      const mine = me ? ` AND cm:creator:"${me}"` : '';
      const r = await this.searchContent(`TYPE:"cm:content" AND cm:created:["${dateStr}" TO NOW]${mine}`, 1);
      this.stats.recentUploads = r?.list?.pagination?.totalItems || 0;
    } catch { this.stats.recentUploads = 0; }
  }

  private async loadRecentDocuments(): Promise<void> {
    try {
      // Only documents the logged-in user has recently worked on (created or
      // modified), not every document in the repository.
      const me = ((this.auth as any).getEcmUsername?.() || '').trim();
      const mine = me ? ` AND (cm:modifier:"${me}" OR cm:creator:"${me}")` : '';
      const r = await this.searchContent(
        `TYPE:"cm:content"${mine}`, 5,
        [{ type: 'FIELD', field: 'cm:modified', ascending: false }]
      );
      if (r?.list?.entries) {
        this.recentDocuments = r.list.entries.map((e: any) => {
          const n = e.entry;
          return {
            id: n.id, name: n.name,
            type: this.getDocType(n.name),
            location: this.formatPath(n.path?.name),
            modifiedDate: new Date(n.modifiedAt),
            modifiedBy: n.modifiedByUser?.displayName || n.modifiedByUser?.id || 'Unknown',
            icon: this.getDocIcon(n.name)
          };
        });
      }
    } catch { this.recentDocuments = []; }
  }

  private async loadDocumentSummary(): Promise<void> {
    const types = [
      { type: 'PDF',    query: 'TYPE:"cm:content" AND cm:name:"*.pdf"',                                            color: '#dc3545' },
      { type: 'Word',   query: 'TYPE:"cm:content" AND (cm:name:"*.doc" OR cm:name:"*.docx")',                     color: '#4A90D9' },
      { type: 'Excel',  query: 'TYPE:"cm:content" AND (cm:name:"*.xls" OR cm:name:"*.xlsx")',                     color: '#28a745' },
      { type: 'Images', query: 'TYPE:"cm:content" AND (cm:name:"*.jpg" OR cm:name:"*.jpeg" OR cm:name:"*.png")',  color: '#f39c12' }
    ];

    const counts: { type: string; count: number; color: string }[] = [];
    let total = 0;
    for (const t of types) {
      try {
        const r = await this.searchContent(t.query, 1);
        const c = r?.list?.pagination?.totalItems || 0;
        counts.push({ type: t.type, count: c, color: t.color });
        total += c;
      } catch { counts.push({ type: t.type, count: 0, color: t.color }); }
    }

    this.totalDocsForChart = total;
    const circumference = 2 * Math.PI * 40;
    let offset = 0;
    this.documentSummary = counts
      .filter(item => item.count > 0)
      .map(item => {
        const pct    = total > 0 ? item.count / total : 0;
        const dash   = circumference * pct;
        const result = {
          ...item,
          percentage: Math.round(pct * 100),
          dashArray:  `${dash.toFixed(1)} ${(circumference - dash).toFixed(1)}`,
          dashOffset: `${(-offset).toFixed(1)}`
        };
        offset += dash;
        return result;
      });
  }

  private formatPath(path: string): string {
    return path
      ? path.replace('/Company Home/User Homes/', '~/').replace('/Company Home/', '/').replace(/^\/+/, '') || '/'
      : '/';
  }
  private getDocType(fn: string): string {
    const e = fn.split('.').pop()?.toLowerCase() || '';
    const m: Record<string,string> = { pdf:'PDF', doc:'Word', docx:'Word', xls:'Excel', xlsx:'Excel', jpg:'Image', jpeg:'Image', png:'Image' };
    return m[e] || 'Other';
  }
  private getDocIcon(fn: string): string {
    const e = fn.split('.').pop()?.toLowerCase() || '';
    const m: Record<string,string> = { pdf:'picture_as_pdf', doc:'description', docx:'description', xls:'table_chart', xlsx:'table_chart', jpg:'image', jpeg:'image', png:'image' };
    return m[e] || 'insert_drive_file';
  }
  getDocIconColor(type: string): string {
    const m: Record<string,string> = { PDF:'#dc3545', Word:'#4A90D9', Excel:'#28a745', Image:'#f39c12' };
    return m[type] || '#6c757d';
  }
  formatRelativeTime(date: Date): string {
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hrs  = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1)  return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs  < 24) return `${hrs}h ago`;
    if (days < 7)  return `${days}d ago`;
    return date.toLocaleDateString();
  }
  navigateTo(route: string): void { this.router.navigate([route]); }
  openMyWork(tab: string): void { this.router.navigate(['/my-work'], { queryParams: tab ? { tab } : {} }); }
  openMyCompleted(): void { this.router.navigate(['/my-completed']); }
  openDocument(doc: RecentDocument): void { this.router.navigate(['/documents'], { queryParams: { preview: doc.id } }); }
  refreshDashboard(): void { this.loadDashboardData(); this.loadAnalytics(); this.loadActiveWorkflows(); }
}